"use client";

import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";

export type ConfirmActionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: ReactNode;
  /** If provided, shows Cancel button. If omitted, only confirm button is shown (e.g. for warnings). */
  cancelLabel?: string | null;
  confirmLabel: string;
  onConfirm: () => void;
  /** Optional className for the content (e.g. max-w-md). */
  contentClassName?: string;
};

/**
 * Reusable confirmation/warning dialog.
 * - Two buttons (Cancel + Confirm) when cancelLabel is provided.
 * - Single button (Confirm only) when cancelLabel is omitted, for informational warnings.
 */
export function ConfirmActionDialog({
  open,
  onOpenChange,
  message,
  cancelLabel,
  confirmLabel,
  onConfirm,
  contentClassName,
}: ConfirmActionDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={contentClassName ?? "p-6"}>
        <div className="flex items-center text-center justify-center my-4">{message}</div>
        <DialogClose>
          <div className="flex justify-between gap-2">
            {cancelLabel != null && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onOpenChange(false)}
              >
                {cancelLabel}
              </Button>
            )}
            <Button className="w-full" onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
